export type SchoolCountry = "usa" | "canada";
export type StudyLevel = "language" | "associate" | "bachelor" | "master" | "certificate" | "diploma";
export type StudyMode = "campus" | "program-hybrid" | "course-hybrid" | "low-residency" | "online-to-campus" | "online-only";

export type SchoolProgram = {
  id: string;
  name: string;
  mongolianName: string;
  credential: string;
  level: StudyLevel;
  field: string;
  duration: string;
  durationMonths?: number;
  mode: StudyMode;
  modeLabel: string;
  modeNote: string;
  internationalStatus: "possible" | "verify" | "not-for-new-international";
  tuition?: { amount: number; currency: "USD" | "CAD"; period: string; academicYear: string };
  annualBudget?: { from: number; to?: number; currency: "USD" | "CAD"; note: string };
};

export type FinderSchool = {
  id: string;
  name: string;
  country: SchoolCountry;
  city: string;
  stateProvince: string;
  locationLabel: string;
  type: "university" | "community-college" | "career-college";
  summary: string;
  featured?: boolean;
  partner?: boolean;
  languagePathway: "available" | "verify" | "not-suitable-for-f1-esl";
  languageNote: string;
  programs: SchoolProgram[];
  officialUrl: string;
  admissionsNote: string;
  campusNote: string;
};

export const LEVEL_LABELS: Record<StudyLevel, string> = {
  language: "Хэлний бэлтгэл",
  associate: "Associate зэрэг",
  bachelor: "Бакалавр",
  master: "Магистр",
  certificate: "Certificate",
  diploma: "Diploma",
};

export const MODE_LABELS: Record<StudyMode, string> = {
  campus: "Кампуст сурах",
  "program-hybrid": "Hybrid — онлайн + кампус",
  "course-hybrid": "Зарим хичээл hybrid",
  "low-residency": "Кампуст цөөн удаа очих",
  "online-to-campus": "Онлайнаар эхлээд кампуст шилжих",
  "online-only": "Бүрэн онлайн",
};

const program = (schoolId: string, data: Omit<SchoolProgram, "id">): SchoolProgram => ({ id: `${schoolId}-${data.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`, ...data });

export const SCHOOL_CATALOG: FinderSchool[] = [
  {
    id: "anu",
    name: "American National University",
    country: "usa",
    city: "Хөтөлбөрөөс хамаарна",
    stateProvince: "АНУ",
    locationLabel: "Сонгосон хөтөлбөрийн кампусаар баталгаажуулна",
    type: "university",
    featured: true,
    summary: "Хэлний бэлтгэлээс эхлээд бизнес, IT чиглэлийн магистрын hybrid хөтөлбөрт үргэлжлүүлэх боломжийг нэг дор нягтална.",
    languagePathway: "available",
    languageNote: "Англи хэлний оноо дутуу бол English Language Institute-ийн ESL хичээлээр бэлтгэж болно. Хугацаа, төлбөрийг сонгосон элсэлтээр батална.",
    programs: [
      program("anu", { name: "English Language Institute", mongolianName: "Англи хэлний бэлтгэл", credential: "English preparation", level: "language", field: "Хэлний бэлтгэл", duration: "Хэлний түвшнээс хамаарна", mode: "campus", modeLabel: "Кампуст бэлтгэх", modeNote: "Оноо дутуу олон улсын өргөдөл гаргагчийг ESL-ээр бэлтгэх боломжийг admission-тэй нягтална.", internationalStatus: "verify" }),
      program("anu", { name: "Master of Business Administration", mongolianName: "Бизнесийн удирдлагын магистр", credential: "Master's degree", level: "master", field: "Бизнес", duration: "Хөтөлбөрийн каталогоор нягтална", mode: "program-hybrid", modeLabel: "Hybrid — онлайн + кампус", modeNote: "Олон улсын оюутны биеэр оролцох residency, кампус ба хуваарийг элсэлт бүрээр батална.", internationalStatus: "verify", tuition: { amount: 430, currency: "USD", period: "1 кредит", academicYear: "2026-03-01 каталог" } }),
      program("anu", { name: "Master of Science in Cybersecurity", mongolianName: "Кибер аюулгүй байдлын магистр", credential: "Master's degree", level: "master", field: "IT / Computer Science", duration: "Хөтөлбөрийн каталогоор нягтална", mode: "program-hybrid", modeLabel: "Hybrid — онлайн + кампус", modeNote: "Бүрэн онлайн хэлбэртэй андуурахгүй; заавал биеэр оролцох нөхцөлийг нягтална.", internationalStatus: "verify", tuition: { amount: 411, currency: "USD", period: "1 кредит", academicYear: "2026-03-01 каталог" } }),
      program("anu", { name: "Master of Science in Information Technology", mongolianName: "Мэдээллийн технологийн магистр", credential: "Master's degree", level: "master", field: "IT / Computer Science", duration: "Хөтөлбөрийн каталогоор нягтална", mode: "program-hybrid", modeLabel: "Hybrid — онлайн + кампус", modeNote: "Кампус, residency болон шинээр элсэх олон улсын оюутны нөхцөлийг батална.", internationalStatus: "verify", tuition: { amount: 422, currency: "USD", period: "1 кредит", academicYear: "2026-03-01 каталог" } }),
      program("anu", { name: "Master of Science in Software Development", mongolianName: "Программ хангамж хөгжүүлэлтийн магистр", credential: "Master's degree", level: "master", field: "IT / Computer Science", duration: "Хөтөлбөрийн каталогоор нягтална", mode: "program-hybrid", modeLabel: "Hybrid — онлайн + кампус", modeNote: "Кампусын биеэр оролцох хуваарь term-ээс хамаарна.", internationalStatus: "verify", tuition: { amount: 440, currency: "USD", period: "1 кредит", academicYear: "2026-03-01 каталог" } }),
      program("anu", { name: "Master of Science in Computer Science and Engineering", mongolianName: "Компьютерын шинжлэх ухаан ба инженерчлэлийн магистр", credential: "Master's degree", level: "master", field: "Инженер / IT", duration: "Хөтөлбөрийн каталогоор нягтална", mode: "program-hybrid", modeLabel: "Hybrid — онлайн + кампус", modeNote: "Хөтөлбөрийн кампус, residency болон admission нөхцөлийг нягтална.", internationalStatus: "verify", tuition: { amount: 372, currency: "USD", period: "1 кредит", academicYear: "2026-03-01 каталог" } }),
    ],
    officialUrl: "https://an.edu/international/",
    admissionsNote: "Дараагийн элсэлт, кампус, тариф болон олон улсын оюутны нөхцөлийг сонгосон хөтөлбөрөөр нягтална.",
    campusNote: "Chicago кампустай мэт үзүүлэхгүй; кампус нь сонгосон хөтөлбөрөөс хамаарна.",
  },
  {
    id: "mcc",
    name: "Midwestern Career College",
    country: "usa",
    city: "Chicago",
    stateProvince: "Illinois",
    locationLabel: "Chicago, Illinois",
    type: "career-college",
    featured: true,
    partner: true,
    summary: "Англи хэлний бэлтгэлээс эхлээд бизнес, нягтлан бодох, маркетинг, IT чиглэлийн career сонголт руу үргэлжлүүлэх боломжтой Chicago сургууль.",
    languagePathway: "available",
    languageNote: "MCC нь F-1 оюутанд ESL, English for Academic Purposes болон Professional English хөтөлбөрөө санал болгодог. Хэлний түвшнээсээ эхэлж degree сонголт руу шилжих боломжийг admission-тэй нягтална.",
    programs: [
      program("mcc", { name: "English as a Second Language", mongolianName: "Англи хэлний бэлтгэл (ESL)", credential: "English preparation", level: "language", field: "Хэлний бэлтгэл", duration: "Түвшнээс хамаарна", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "Beginner–Advanced ESL, Academic/Professional English сонголттой. Одоогийн улирлын төлбөрийг батална.", internationalStatus: "possible" }),
      program("mcc", { name: "Business Administration — Associate of Applied Science", mongolianName: "Бизнесийн удирдлага", credential: "AAS degree", level: "associate", field: "Бизнес", duration: "2 × 16 долоо хоногийн улиралтай жил", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "F-1 оюутанд нээлттэй, OPT/CPT eligibility-г MCC тэмдэглэсэн.", internationalStatus: "possible", tuition: { amount: 2800, currency: "USD", period: "12 кредит · 1 улирал", academicYear: "2026–27" } }),
      program("mcc", { name: "Accounting — Associate of Applied Science", mongolianName: "Нягтлан бодох бүртгэл", credential: "AAS degree", level: "associate", field: "Нягтлан бодох / Бизнес", duration: "2 × 16 долоо хоногийн улиралтай жил", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "F-1 оюутанд нээлттэй. Learning Management System хураамж $200/улирал тусдаа.", internationalStatus: "possible", tuition: { amount: 2800, currency: "USD", period: "12 кредит · 1 улирал", academicYear: "2026–27" } }),
      program("mcc", { name: "Marketing — Associate of Applied Science", mongolianName: "Маркетинг", credential: "AAS degree", level: "associate", field: "Маркетинг / Бизнес", duration: "2 × 16 долоо хоногийн улиралтай жил", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "F-1 оюутанд нээлттэй. Learning Management System хураамж $200/улирал тусдаа.", internationalStatus: "possible", tuition: { amount: 2800, currency: "USD", period: "12 кредит · 1 улирал", academicYear: "2026–27" } }),
      program("mcc", { name: "Information Technology — Associate of Applied Science", mongolianName: "Мэдээллийн технологи (IT)", credential: "AAS degree", level: "associate", field: "IT / Computer Science", duration: "2 × 16 долоо хоногийн улиралтай жил", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "F-1 оюутанд нээлттэй. $250 technology fee, $200 LMS fee/улирал тусдаа; эхний хоёр бүтэн улиралд $1,500 tuition discount нөхцөлтэй байж болно.", internationalStatus: "possible", tuition: { amount: 5000, currency: "USD", period: "12+ кредит · 1 улирал", academicYear: "2026–27" } }),
    ],
    officialUrl: "https://mccollege.edu/international-students/",
    admissionsNote: "Хөтөлбөр, clinical placement болон admission нөхцөлийг сонгохоос өмнө нягтална.",
    campusNote: "Чикаго хот.",
  },
  {
    id: "neiu",
    name: "Northeastern Illinois University",
    country: "usa",
    city: "Chicago",
    stateProvince: "Illinois",
    locationLabel: "Chicago, Illinois",
    type: "university",
    summary: "Чикаго дахь улсын их сургууль. Олон улсын элсэлт болон сонгосон мэргэжлийн шаардлагыг хөтөлбөрөөр нь шалгана.",
    languagePathway: "available",
    languageNote: "Intensive English Program болон зэрэг олгох хөтөлбөрийн нөхцөлийг ялгаж сонгоно.",
    programs: [
      program("neiu", { name: "Intensive English Program", mongolianName: "Эрчимжүүлсэн англи хэл", credential: "Language preparation", level: "language", field: "Хэлний бэлтгэл", duration: "Түвшнээс хамаарна", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "Түвшин тогтоох болон дараагийн зэрэгт шилжих нөхцөлийг нягтална.", internationalStatus: "verify" }),
      program("neiu", { name: "Undergraduate and Graduate Programs", mongolianName: "Бакалавр ба магистрын сонголтууд", credential: "Degree", level: "bachelor", field: "Бизнес / Боловсрол / Шинжлэх ухаан", duration: "Зэргээс хамаарна", mode: "course-hybrid", modeLabel: "Зарим хичээл hybrid", modeNote: "Зарим hybrid хичээл нь бүхэл хөтөлбөрийг low-residency болгохгүй.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.neiu.edu/admissions/international-admissions",
    admissionsNote: "Хөтөлбөрийн хэлний босго, deadline, тарифыг admission cycle бүрээр нягтална.",
    campusNote: "Chicago, Illinois.",
  },
  {
    id: "depaul",
    name: "DePaul University",
    country: "usa",
    city: "Chicago",
    stateProvince: "Illinois",
    locationLabel: "Chicago, Illinois",
    type: "university",
    summary: "Chicago дахь их сургууль. English Language Academy/Intensive English бэлтгэлээс эхлээд бизнес, маркетинг, computer science чиглэлийн degree сонголтыг нягтална.",
    languagePathway: "available",
    languageNote: "Бакалаврын академик шаардлага хангасан ч хэлний оноо дутуу бол нөхцөлт элсэлт болон ELA/IEP хэлний бэлтгэлийг авч үзэх боломжтой. Магистрын хэлний нөхцөл нь хөтөлбөр бүрээр өөр.",
    programs: [
      program("depaul", { name: "English Language Academy / Intensive English Program", mongolianName: "Англи хэлний бэлтгэл", credential: "English preparation", level: "language", field: "Хэлний бэлтгэл", duration: "Түвшнээс хамаарна", mode: "campus", modeLabel: "Кампуст бэлтгэх", modeNote: "ELA/IEP болон degree admission-ийн нөхцөлийг сонгосон түвшнээр нь нягтална. Төлбөрийг тухайн intake-аар батална.", internationalStatus: "verify" }),
      program("depaul", { name: "Master of Business Administration", mongolianName: "Бизнесийн удирдлагын магистр", credential: "Master's degree", level: "master", field: "Бизнес", duration: "Full-time 15 сар · Part-time 18–24 сар", mode: "course-hybrid", modeLabel: "Кампус / online-hybrid сонголт", modeNote: "Remote online-hybrid, Flex болон кампуст hybrid хэлбэрийг хооронд нь андуурахгүй. Олон улсын оюутны нөхцөлийг тусад нь батална.", internationalStatus: "verify", tuition: { amount: 1228, currency: "USD", period: "1 кредит", academicYear: "2025–26" } }),
      program("depaul", { name: "MBA — Accountancy specialization", mongolianName: "MBA · Нягтлан бодох чиглэл", credential: "Master's degree", level: "master", field: "Нягтлан бодох / Бизнес", duration: "Хөтөлбөрийн хуваариар нягтална", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "Advanced accounting, audit, forensic accounting, information systems чиглэлтэй. Admission болон format-ийг нягтална.", internationalStatus: "verify", tuition: { amount: 1228, currency: "USD", period: "1 кредит", academicYear: "2025–26" } }),
      program("depaul", { name: "MBA — Marketing specialization", mongolianName: "MBA · Маркетингийн чиглэл", credential: "Master's degree", level: "master", field: "Маркетинг / Бизнес", duration: "Хөтөлбөрийн хуваариар нягтална", mode: "course-hybrid", modeLabel: "Кампус / online-hybrid сонголт", modeNote: "Market research, category management, business development, digital marketing чиглэлтэй.", internationalStatus: "verify", tuition: { amount: 1228, currency: "USD", period: "1 кредит", academicYear: "2025–26" } }),
      program("depaul", { name: "Computer Science (MS)", mongolianName: "Компьютерын шинжлэх ухааны магистр", credential: "Master's degree", level: "master", field: "IT / Computer Science", duration: "48–72 quarter credit; суурь мэдлэгээс хамаарна", mode: "campus", modeLabel: "Хөтөлбөрөөр нягтална", modeNote: "Software development эсвэл computer science research чиглэлд бэлтгэнэ. Формат ба олон улсын элсэлтийг admission-тэй батална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.depaul.edu/admission/international-admission/resources",
    admissionsNote: "Хөтөлбөрийн босго, тэтгэлэг, international application deadline-ийг тусад нь нягтална.",
    campusNote: "Lincoln Park болон Loop кампусуудын хөтөлбөрийг ялгана.",
  },
  {
    id: "cod",
    name: "College of DuPage",
    country: "usa",
    city: "Glen Ellyn",
    stateProvince: "Illinois",
    locationLabel: "Chicago орчим — Glen Ellyn, Illinois",
    type: "community-college",
    summary: "Чикаго хотын захын community college. Associate болон transfer сонголтыг тусдаа нягтална.",
    languagePathway: "verify",
    languageNote: "Хэлний бэлтгэл болон degree admission-ийн олон улсын оюутны нөхцөлийг ялгана.",
    programs: [
      program("cod", { name: "Associate Degree and Transfer Programs", mongolianName: "Associate зэрэг ба шилжих хөтөлбөрүүд", credential: "Associate degree", level: "associate", field: "Бизнес / IT / Шинжлэх ухаан", duration: "Ихэвчлэн 2 жил; кредитээс хамаарна", durationMonths: 24, mode: "course-hybrid", modeLabel: "Зарим хичээл hybrid", modeNote: "Term бүрийн сургалтын ачаалал, international course load-ийг нягтална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.cod.edu/admissions/international/",
    admissionsNote: "Төлбөр, I-20 санхүүгийн шаардлага болон эхлэх улирлыг албан ёсны хуудсаар батална.",
    campusNote: "Glen Ellyn нь Chicago хотын төв биш.",
  },
  {
    id: "harper",
    name: "Harper College",
    country: "usa",
    city: "Palatine",
    stateProvince: "Illinois",
    locationLabel: "Chicago орчим — Palatine, Illinois",
    type: "community-college",
    summary: "Chicago орчмын community college. Associate, certificate болон transfer сонголтыг хөтөлбөрийн түвшнээр нь шалгана.",
    languagePathway: "verify",
    languageNote: "International admission болон хэлний бэлтгэлийн боломжийг тусад нь нягтална.",
    programs: [
      program("harper", { name: "Associate Degree Programs", mongolianName: "Associate зэргийн хөтөлбөрүүд", credential: "Associate degree", level: "associate", field: "Бизнес / IT / Инженер / Эрүүл мэнд", duration: "Кредитийн ачааллаас хамаарна", durationMonths: 24, mode: "course-hybrid", modeLabel: "Зарим хичээл blended/hybrid", modeNote: "Blended хэлбэр нь заавал кампуст очих бүрэлдэхүүнтэй байж болно; Flex-тэй андуурахгүй.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.harpercollege.edu/international/",
    admissionsNote: "Элсэлтийн босго, тариф, deadline-ийг жил бүрийн admission мэдээллээр батална.",
    campusNote: "Palatine нь Chicago хотын захын байршил.",
  },
  {
    id: "oakton",
    name: "Oakton College",
    country: "usa",
    city: "Des Plaines / Skokie",
    stateProvince: "Illinois",
    locationLabel: "Chicago орчим — Des Plaines / Skokie, Illinois",
    type: "community-college",
    summary: "Chicago орчмын community college. International student-ийн хичээлийн ачаалал, кампусын оролцооны нөхцөлийг тусад нь баримтална.",
    languagePathway: "not-suitable-for-f1-esl",
    languageNote: "Ерөнхий ESL хичээл байгаагаар нь F-1-д зориулсан бүтэн цагийн ESL major гэж харуулахгүй.",
    programs: [
      program("oakton", { name: "Associate Degree Programs", mongolianName: "Associate зэргийн хөтөлбөрүүд", credential: "Associate degree", level: "associate", field: "Бизнес / Computer Science / Engineering Transfer", duration: "Кредитийн ачааллаас хамаарна", durationMonths: 24, mode: "course-hybrid", modeLabel: "Hybrid хичээл сонгох боломжтой", modeNote: "International student-д доод тал нь 9 кредит on-campus эсвэл hybrid байх нөхцөлийг шалгана.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.oakton.edu/admissions/apply-to-oakton/international-student/",
    admissionsNote: "F-1 course load, program admission, тариф болон эхлэх улирлыг нягтална.",
    campusNote: "Des Plaines / Skokie кампусуудыг тусад нь шалгана.",
  },
  {
    id: "avila",
    name: "Avila University",
    country: "usa",
    city: "Kansas City",
    stateProvince: "Missouri",
    locationLabel: "Kansas City, Missouri",
    type: "university",
    featured: true,
    partner: true,
    summary: "Kansas City дахь их сургууль. Low-residency болон кампусын сонголтыг олон улсын өргөдөл гаргагчийн байршлаар ялгана.",
    languagePathway: "verify",
    languageNote: "Хэлний болон шууд degree admission-ийн нөхцөлийг тусад нь нягтална.",
    programs: [
      program("avila", { name: "Master of Business Administration", mongolianName: "Бизнесийн удирдлагын магистр", credential: "Master's degree", level: "master", field: "Бизнес", duration: "Хөтөлбөрийн хуваарийг нягтална", mode: "low-residency", modeLabel: "Кампуст цөөн удаа очих", modeNote: "Low-residency хөтөлбөр нь АНУ дотроос өргөдөл гаргах хязгаарлалттай байж болох тул Монголоос шинээр шууд тохирно гэж санал болгохгүй.", internationalStatus: "not-for-new-international" }),
      program("avila", { name: "Kansas City Campus Programs", mongolianName: "Kansas City кампусын хөтөлбөрүүд", credential: "Degree", level: "bachelor", field: "Бизнес / Технологи", duration: "Зэргээс хамаарна", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "Шинэ олон улсын оюутны сонголтыг кампусын admission-р нягтална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.avila.edu/admissions/international-students/",
    admissionsNote: "Low-residency болон Kansas City campus admission-ийг нэгтгэхгүй.",
    campusNote: "Kansas City, Missouri.",
  },
  {
    id: "smc",
    name: "Santa Monica College",
    country: "usa",
    city: "Santa Monica",
    stateProvince: "California",
    locationLabel: "Los Angeles орчим — Santa Monica, California",
    type: "community-college",
    summary: "Los Angeles орчмын community college. Associate, transfer болон улирлын hybrid course сонголтыг тусад нь нягтална.",
    languagePathway: "verify",
    languageNote: "International education-ийн хэлний болон degree pathway нөхцөлийг сонгосон хөтөлбөрөөр нягтална.",
    programs: [
      program("smc", { name: "Associate and Transfer Programs", mongolianName: "Associate зэрэг ба transfer хөтөлбөр", credential: "Associate degree", level: "associate", field: "Бизнес / Урлаг / Шинжлэх ухаан", duration: "Кредитээс хамаарна", durationMonths: 24, mode: "course-hybrid", modeLabel: "Hybrid хичээл сонгох боломжтой", modeNote: "Улирлын on-ground/hybrid сонголт ба full-time load-ийг нягтална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.smc.edu/student-support/international-education/",
    admissionsNote: "International application cycle, transfer pathway, tuition ба кампусын schedule-ийг нягтална.",
    campusNote: "Santa Monica нь Los Angeles хотын төвөөс тусдаа байршил.",
  },
  {
    id: "pcc",
    name: "Pasadena City College",
    country: "usa",
    city: "Pasadena",
    stateProvince: "California",
    locationLabel: "Los Angeles орчим — Pasadena, California",
    type: "community-college",
    summary: "Los Angeles орчмын community college. Associate болон transfer хөтөлбөрийн улирлын course load-ийг нягталж сонгоно.",
    languagePathway: "verify",
    languageNote: "Хэлний бэлтгэл, олон улсын элсэлт, дараагийн degree admission-ийн нөхцөлийг ялгана.",
    programs: [
      program("pcc", { name: "Associate Degree Programs", mongolianName: "Associate зэргийн хөтөлбөрүүд", credential: "Associate degree", level: "associate", field: "Бизнес / STEM / Урлаг", duration: "Кредитээс хамаарна", durationMonths: 24, mode: "course-hybrid", modeLabel: "Hybrid хичээл сонгох боломжтой", modeNote: "F-1 оюутны 12 нэгжийн on-campus/hybrid ачааллын нөхцөлийг улирлаар нягтална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.pasadena.edu/academics/international-students/index.php",
    admissionsNote: "International deadline, I-20 санхүү болон program requirement-ийг батална.",
    campusNote: "Pasadena нь Los Angeles орчмын хот.",
  },
  {
    id: "gcc",
    name: "Glendale Community College",
    country: "usa",
    city: "Glendale",
    stateProvince: "California",
    locationLabel: "Los Angeles орчим — Glendale, California",
    type: "community-college",
    summary: "California мужийн Glendale дахь community college. Arizona дахь ижил нэртэй сургуулиас ялгана.",
    languagePathway: "verify",
    languageNote: "International элсэлт, хэлний бэлтгэл болон degree program-ийн нөхцөлийг тусад нь нягтална.",
    programs: [
      program("gcc", { name: "Associate Degree Programs", mongolianName: "Associate зэргийн хөтөлбөрүүд", credential: "Associate degree", level: "associate", field: "Бизнес / Технологи / Урлаг", duration: "Кредитээс хамаарна", durationMonths: 24, mode: "course-hybrid", modeLabel: "Hybrid хичээл сонгох боломжтой", modeNote: "Биеэр оролцох хэсэгтэй hybrid хичээлийг remote/Zoom-only ангилалтай андуурахгүй.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.glendale.edu/apply/international-students/index.html",
    admissionsNote: "Хөтөлбөр, international deadline, tuition болон schedule-ийг батална.",
    campusNote: "Glendale, California — Arizona дахь ижил нэртэй сургууль биш.",
  },
  {
    id: "seattle-central",
    name: "Seattle Central College",
    country: "usa",
    city: "Seattle",
    stateProvince: "Washington",
    locationLabel: "Seattle, Washington",
    type: "community-college",
    summary: "Seattle Colleges-ийн нэг кампус. Онлайнаар эхлээд кампуст үргэлжлэх зам болон кампусын degree сонголтыг ялгана.",
    languagePathway: "verify",
    languageNote: "Хэлний болон degree сонголтын олон улсын оюутны нөхцөлийг кампусаар шалгана.",
    programs: [
      program("seattle-central", { name: "Online-to-Campus Pathway", mongolianName: "Онлайнаар эхлээд кампуст үргэлжлэх зам", credential: "Pathway", level: "associate", field: "Олон чиглэл", duration: "Хөтөлбөрөөс хамаарна", mode: "online-to-campus", modeLabel: "Онлайнаар эхлээд кампуст шилжих", modeNote: "Энэ нь low-residency degree гэсэн үг биш; сонгосон хөтөлбөр, кампусын schedule-ийг нягтална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://intl.seattlecolleges.edu/programs",
    admissionsNote: "Элсэлт, campus program, tuition болон визийн хугацааг нягтална.",
    campusNote: "Seattle, Washington — Washington D.C.-ээс тусдаа.",
  },
  {
    id: "north-seattle",
    name: "North Seattle College",
    country: "usa",
    city: "Seattle",
    stateProvince: "Washington",
    locationLabel: "Seattle, Washington",
    type: "community-college",
    summary: "Seattle Colleges-ийн нэг кампус. Кампусын degree сонголт болон онлайнаар эхлээд шилжих дараалсан замыг тусад нь шалгана.",
    languagePathway: "verify",
    languageNote: "Хэлний бэлтгэл, campus program, international admission-ийг сонгосон замаар нягтална.",
    programs: [
      program("north-seattle", { name: "Online-to-Campus Pathway", mongolianName: "Онлайнаар эхлээд кампуст үргэлжлэх зам", credential: "Pathway", level: "associate", field: "Олон чиглэл", duration: "Хөтөлбөрөөс хамаарна", mode: "online-to-campus", modeLabel: "Онлайнаар эхлээд кампуст шилжих", modeNote: "Full degree hybrid гэж дүгнэхгүй; кампусын бодит schedule-ийг нягтална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://intl.seattlecolleges.edu/programs",
    admissionsNote: "Элсэлт, campus program, tuition болон визийн нөхцөлийг нягтална.",
    campusNote: "Seattle, Washington.",
  },
  {
    id: "south-seattle",
    name: "South Seattle College",
    country: "usa",
    city: "Seattle",
    stateProvince: "Washington",
    locationLabel: "Seattle, Washington",
    type: "community-college",
    summary: "Seattle Colleges-ийн нэг кампус. Зөвхөн тодорхой замд онлайнаар эхлээд кампуст шилжих дараалал байж болно.",
    languagePathway: "verify",
    languageNote: "SCIE, short-term certificate, career training болон гадаадаас online эхлэх эрхийг тус тусад нь шалгана.",
    programs: [
      program("south-seattle", { name: "Online-to-Campus Pathway", mongolianName: "Онлайнаар эхлээд кампуст үргэлжлэх зам", credential: "Pathway", level: "associate", field: "Олон чиглэл", duration: "Хөтөлбөрөөс хамаарна", mode: "online-to-campus", modeLabel: "Онлайнаар эхлээд кампуст шилжих", modeNote: "Кампусын degree, short-term certificate болон online эхлэх нөхцөлийг нэгтгэхгүй.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://intl.seattlecolleges.edu/programs",
    admissionsNote: "Сонгосон program-ийн олон улсын элсэлт, кампусын schedule, tuition-ийг нягтална.",
    campusNote: "Seattle, Washington.",
  },
  {
    id: "potomac",
    name: "University of the Potomac",
    country: "usa",
    city: "Washington, D.C. / Virginia",
    stateProvince: "Washington, D.C. / Virginia",
    locationLabel: "Washington, D.C. болон Virginia кампусыг тусад нь сонгоно",
    type: "university",
    summary: "Washington, D.C. ба Virginia байршлын сонголттой. Hybrid degree-ийн кампус, хөтөлбөр, олон улсын оюутны нөхцөлийг тусад нь нягтална.",
    languagePathway: "verify",
    languageNote: "Хэлний босго болон pathway боломжийг degree admission-ээс тусад нь нягтална.",
    programs: [
      program("potomac", { name: "Hybrid Degree Programs", mongolianName: "Hybrid degree хөтөлбөрүүд", credential: "Degree", level: "bachelor", field: "Бизнес / IT", duration: "Хөтөлбөрөөс хамаарна", mode: "program-hybrid", modeLabel: "Hybrid — онлайн + кампус", modeNote: "Campus, attendance frequency, D.C./Virginia байршил, шинээр F-1 элсэх нөхцөлийг тогтоохгүйгээр амлахгүй.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://potomac.edu/hybrid-degree-programs/",
    admissionsNote: "Campus, intake, tuition ба international eligibility-ийг сонгосон degree-ээр батална.",
    campusNote: "Washington, D.C. болон Virginia-г нэг байршил мэт бүү үзүүл.",
  },
  {
    id: "niagara",
    name: "Niagara College",
    country: "canada",
    city: "Welland / Niagara-on-the-Lake",
    stateProvince: "Ontario",
    locationLabel: "Niagara Region, Ontario",
    type: "community-college",
    summary: "Ontario дахь college. Сонгосон program-ийн кампус, олон улсын элсэлт, төлбөр, PGWP болон эхлэх улирлыг тусад нь нягтална.",
    languagePathway: "verify",
    languageNote: "Хэлний pathway, degree/diploma admission болон study permit нөхцөлийг ялгана.",
    programs: [
      program("niagara", { name: "Full-Time International Programs", mongolianName: "Бүрэн цагийн international хөтөлбөрүүд", credential: "Diploma / Degree", level: "diploma", field: "Бизнес / Hospitality / Технологи", duration: "Хөтөлбөрөөс хамаарна", mode: "course-hybrid", modeLabel: "Зарим хичээл hybrid", modeNote: "Хөтөлбөрийн timetable, тодорхой кампус, олон улсын admission-г нягтална.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://international.niagaracollege.ca/programs/full-time-studies",
    admissionsNote: "DLI, PGWP, intake, tuition болон campus-ийг сонгосон program-оор батална.",
    campusNote: "Welland ба Niagara-on-the-Lake кампусын хөтөлбөрийг ялгана.",
  },
  {
    id: "ucw",
    name: "University Canada West",
    country: "canada",
    city: "Vancouver",
    stateProvince: "British Columbia",
    locationLabel: "Vancouver, British Columbia",
    type: "university",
    summary: "Vancouver дахь их сургууль. Тодорхой MBA хөтөлбөрийн campus + online guided learning хэлбэрийг formal online сонголтоос тусад нь харуулна.",
    languagePathway: "available",
    languageNote: "University Access Program-ийн хэрэглэх түвшин, degree admission-ийн нөхцөлийг тусад нь нягтална.",
    programs: [
      program("ucw", { name: "Master of Business Administration", mongolianName: "Бизнесийн удирдлагын магистр", credential: "Master's degree", level: "master", field: "Бизнес", duration: "Хөтөлбөрийн академик хуваариар нягтална", mode: "program-hybrid", modeLabel: "MBA · Hybrid", modeNote: "Campus болон online guided learning хосолно. Formal online MBA-г тусдаа сонголт гэж үзнэ; шинэ international eligibility-ийг батална.", internationalStatus: "verify" }),
      program("ucw", { name: "University Access Program", mongolianName: "Их сургуулийн элсэлтийн бэлтгэл", credential: "Preparation program", level: "language", field: "Хэлний бэлтгэл", duration: "Түвшнээс хамаарна", mode: "campus", modeLabel: "Кампуст сурах", modeNote: "Undergraduate entry-д чиглэсэн нөхцөлийг graduate admission-д автоматаар адилтгахгүй.", internationalStatus: "verify" }),
    ],
    officialUrl: "https://www.ucanwest.ca/admissions",
    admissionsNote: "Program, campus, tariff, intake ба study permit eligibility-ийг албан ёсны admission-аар нягтална.",
    campusNote: "Vancouver, British Columbia.",
  },
];

export const allPrograms = SCHOOL_CATALOG.flatMap((school) => school.programs.map((item) => ({ ...item, school })));

export const RECOMMENDED_SCHOOL_IDS = ["mcc", "avila", "anu"] as const;

export type RecommendedSchoolContent = {
  image: string;
  noScorePath: string;
  majors: string[];
  tuition: string;
  tuitionNote: string;
  duration: string;
  scholarship: string;
  hybrid?: boolean;
};

export const RECOMMENDED_SCHOOL_CONTENT: Record<(typeof RECOMMENDED_SCHOOL_IDS)[number], RecommendedSchoolContent> = {
  mcc: {
    image: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/uOJAtIVRbGJqcPmW.jpg",
    noScorePath: "IELTS/TOEFL оноогүй бол сургуулийн дотоод шалгалтаар түвшнээ тогтоолгох боломжтой.",
    majors: ["Бизнес", "Маркетинг", "Нягтлан бодох бүртгэл", "Мэдээллийн технологи"],
    tuition: "Жилийн төлбөрийг лавлах",
    tuitionNote: "Сонгосон AAS, кредит ба хураамжаар батална. Байр, амьжиргаа ороогүй.",
    duration: "AAS хөтөлбөрөөр ойролцоогоор 2 жил",
    scholarship: "Тэтгэлэг: Одоогийн боломжийг нягталж байна",
  },
  avila: {
    image: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/JgfRIQnCakpGoiMG.jpg",
    noScorePath: "Хэлний оноогүй эхлэх боломж болон хэлний бэлтгэлийг сонгосон degree-ээр нь батална.",
    majors: ["Бизнесийн удирдлага", "Компьютерын шинжлэх ухаан", "Кибер аюулгүй байдал", "Сэтгэл судлал"],
    tuition: "Жилийн төлбөрийг лавлах",
    tuitionNote: "Кампусын сонгосон бакалавр эсвэл магистрын тарифаар батална. Байр, амьжиргаа ороогүй.",
    duration: "Сонгосон бакалавр, магистрын хөтөлбөрөөс хамаарна",
    scholarship: "Тэтгэлэг: Боломжтой — нөхцөлийг тухайн элсэлтээр батална",
  },
  anu: {
    image: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663375899735/zVXaKtMkqUxDRZtL.jpg",
    noScorePath: "Хэлний бэлтгэл эсвэл зөвшөөрөгдсөн өөр үнэлгээний замаар эхлэх боломжоо шалгуулна.",
    majors: ["Бизнес", "Кибер аюулгүй байдал", "Мэдээллийн технологи", "Компьютерын шинжлэх ухаан"],
    tuition: "$16,260 USD",
    tuitionNote: "2027 оны 5 term-ийн жишиг төсөв (2025-12 тариф, BBA жишээ). Байр, амьжиргаа ороогүй.",
    duration: "Бакалавр ойролцоогоор 3–3.5 жил; магистр 1.25–1.5 жил",
    scholarship: "Тэтгэлэг: Боломжтой эсэхийг тухайн элсэлтээр нягтална",
    hybrid: true,
  },
};

export function getRecommendedSchools() {
  return RECOMMENDED_SCHOOL_IDS.map((id) => {
    const school = SCHOOL_CATALOG.find((item) => item.id === id);
    return school ? { school, ...RECOMMENDED_SCHOOL_CONTENT[id] } : undefined;
  }).filter((item): item is { school: FinderSchool } & RecommendedSchoolContent => Boolean(item));
}

export function getSchoolById(id: string) {
  return SCHOOL_CATALOG.find((school) => school.id === id);
}

export function getProgramById(id: string) {
  return allPrograms.find((programItem) => programItem.id === id);
}

export function addCompareProgram(programId: string) {
  const current = JSON.parse(window.localStorage.getItem("udm-school-compare") ?? "[]") as string[];
  const next = Array.from(new Set([...current, programId])).slice(-3);
  window.localStorage.setItem("udm-school-compare", JSON.stringify(next));
  return next;
}

export function getComparePrograms() {
  try {
    const ids = JSON.parse(window.localStorage.getItem("udm-school-compare") ?? "[]") as string[];
    return ids.map(getProgramById).filter((item): item is NonNullable<typeof item> => Boolean(item));
  } catch {
    return [];
  }
}

export function clearComparePrograms() {
  window.localStorage.removeItem("udm-school-compare");
}

export function formatMoney(amount: number, currency: "USD" | "CAD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
}
